import { lazy, Suspense, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import {
  CircleCheck,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coins,
  Gauge,
  HandCoins,
  Timer,
  Undo2,
  Wallet,
} from 'lucide-react'
import { env } from '@/lib/env'
import { formatDate } from '@/lib/datetime'
import { formatDuration, formatMinorCurrency, formatNumber, pluralize } from '@/lib/format'
import { cn } from '@/lib/cn'
import { queryKeys } from '@/lib/query-client'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { CardSkeleton, Skeleton } from '@/components/ui/Skeleton'
import { ErrorState } from '@/components/ui/States'
import {
  DEFAULT_REPORT_RANGE,
  fetchBookingReport,
  isReportRangeKey,
  REPORT_RANGE_KEYS,
  REPORT_RANGES,
  type ReportRangeKey,
} from './api'
import styles from './ReportingPage.module.css'

import { EmployeeResponseTable } from './EmployeeResponseTable'

const OutcomeDonut = lazy(() => import('./OutcomeDonut').then((module) => ({ default: module.OutcomeDonut })))
const ServiceTypeChart = lazy(() => import('./ServiceTypeChart').then((module) => ({ default: module.ServiceTypeChart })))
const OutcomeTrendChart = lazy(() => import('./OutcomeTrendChart').then((module) => ({ default: module.OutcomeTrendChart })))
const ResponseTimeChart = lazy(() => import('./ResponseTimeChart').then((module) => ({ default: module.ResponseTimeChart })))

type Tone = 'brand' | 'success' | 'danger' | 'warning' | 'info' | 'neutral'

interface StatBreakdownItem {
  label: string
  value: string
  tone?: Tone
}

interface ReportStatProps {
  label: string
  value: string
  icon: LucideIcon
  tone?: Tone
  hint?: string
  breakdown?: StatBreakdownItem[]
}

function ReportStat({ label, value, icon: Icon, tone = 'neutral', hint, breakdown }: ReportStatProps) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statTop}>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statIcon} data-tone={tone === 'neutral' ? undefined : tone}>
          <Icon aria-hidden="true" />
        </span>
      </div>
      <p className={styles.statValue}>{value}</p>
      {breakdown && breakdown.length > 0 ? (
        <ul className={styles.statBreakdown} role="list">
          {breakdown.map((item) => (
            <li key={item.label} className={styles.statBreakdownItem} data-tone={item.tone ?? 'neutral'}>
              <span className={styles.statBreakdownValue}>{item.value}</span>
              {item.label}
            </li>
          ))}
        </ul>
      ) : hint ? (
        <p className={styles.statHint}>{hint}</p>
      ) : null}
    </div>
  )
}

function ChartFallback({ height = 220 }: { height?: number }) {
  return <Skeleton height={height} radius="var(--radius-md)" />
}

const percent = (rate: number): string => `${Math.round(rate * 100)}%`

export function ReportingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawRange = searchParams.get('range')
  const rangeKey: ReportRangeKey = isReportRangeKey(rawRange) ? rawRange : DEFAULT_REPORT_RANGE

  const reportQuery = useQuery({
    queryKey: queryKeys.reporting.bookings(rangeKey),
    queryFn: () => fetchBookingReport(rangeKey),
    // Keep the previous range's figures on screen while the next range loads, so
    // switching periods updates in place instead of flashing skeletons.
    placeholderData: (previous) => previous,
  })

  useEffect(() => {
    document.title = `Reporting · ${env.appName} Operations`
  }, [])

  const setRange = (key: ReportRangeKey) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('range', key)
        return next
      },
      { replace: true },
    )
  }

  const report = reportQuery.data
  const loading = reportQuery.isLoading || !report
  const rangeMeta = REPORT_RANGES[rangeKey]

  const rangeControl = (
    <div className={styles.rangeControl} role="group" aria-label="Report period">
      {REPORT_RANGE_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          className={cn(styles.rangeButton, key === rangeKey && styles.rangeButtonActive)}
          aria-pressed={key === rangeKey}
          onClick={() => setRange(key)}
        >
          {REPORT_RANGES[key].shortLabel}
        </button>
      ))}
    </div>
  )

  return (
    <>
      <PageHeader
        title="Reporting"
        description="Booking outcomes and revenue, counted from the production database. Choose a period; every figure and chart below follows it."
        actions={
          <>
            {rangeControl}
            <Button
              variant="secondary"
              size="md"
              loading={reportQuery.isFetching}
              onClick={() => void reportQuery.refetch()}
            >
              Refresh
            </Button>
          </>
        }
      />

      {reportQuery.isError ? (
        <Card>
          <ErrorState error={reportQuery.error} onRetry={() => void reportQuery.refetch()} />
        </Card>
      ) : (
        <>
          <p className={styles.periodBar}>
            <span className={styles.periodRange}>
              {report
                ? report.from
                  ? `${formatDate(report.from)} – ${formatDate(report.to)}`
                  : `All time · up to ${formatDate(report.to)}`
                : rangeMeta.label}
            </span>
            {report ? (
              <span className={styles.periodFunnel}>
                <strong>{formatNumber(report.received)}</strong> received →{' '}
                <strong>{formatNumber(report.confirmed)}</strong> confirmed →{' '}
                <strong>{formatNumber(report.fulfilled)}</strong> fulfilled
              </span>
            ) : null}
          </p>

          <h2 className={styles.sectionTitle}>Bookings</h2>
          <section className={styles.kpiGrid} aria-label="Booking figures">
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} height={132} />)
            ) : (
              <>
                <ReportStat
                  label="Requests received"
                  value={formatNumber(report.received)}
                  icon={ClipboardList}
                  tone="brand"
                  hint={`in ${rangeMeta.label.toLowerCase()}`}
                />
                <ReportStat
                  label="Fulfilled"
                  value={formatNumber(report.fulfilled)}
                  icon={CircleCheck}
                  tone="success"
                  hint={`${percent(report.fulfillmentRate)} fulfillment rate`}
                />
                <ReportStat
                  label="Cancelled"
                  value={formatNumber(report.cancelledTotal)}
                  icon={CircleX}
                  tone="danger"
                  breakdown={[
                    { label: 'customer', value: formatNumber(report.cancelledByCustomer), tone: 'warning' },
                    { label: 'staff', value: formatNumber(report.cancelledByStaff), tone: 'danger' },
                    { label: 'system', value: formatNumber(report.cancelledBySystem), tone: 'neutral' },
                  ]}
                />
                <ReportStat
                  label="Still active"
                  value={formatNumber(report.active)}
                  icon={Clock}
                  tone="info"
                  hint="pending or in progress"
                />
              </>
            )}
          </section>

          <h2 className={styles.sectionTitle}>Money</h2>
          <section className={styles.kpiGrid} aria-label="Money figures">
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} height={132} />)
            ) : (
              <>
                <ReportStat
                  label="Fulfilled revenue"
                  value={formatMinorCurrency(report.money.fulfilledRevenueMinor)}
                  icon={Wallet}
                  tone="success"
                  breakdown={[
                    { label: 'cash', value: formatMinorCurrency(report.money.cashRevenueMinor), tone: 'info' },
                    { label: 'card', value: formatMinorCurrency(report.money.cardRevenueMinor), tone: 'warning' },
                  ]}
                />
                <ReportStat
                  label="Refunds paid"
                  value={formatMinorCurrency(report.money.refundsPaidMinor)}
                  icon={Undo2}
                  tone="warning"
                  hint={`${formatNumber(report.money.refundsCount)} ${pluralize(report.money.refundsCount, 'refund')} in period`}
                />
                <ReportStat
                  label="Outstanding penalties"
                  value={formatMinorCurrency(report.money.outstandingPenaltiesMinor)}
                  icon={HandCoins}
                  tone="danger"
                  hint={`${formatNumber(report.money.outstandingPenaltiesCount)} owed · live balance`}
                />
                <ReportStat
                  label="Points spent"
                  value={formatMinorCurrency(report.money.pointsSpentMinor)}
                  icon={Coins}
                  tone="neutral"
                  hint={report.money.pointsSpentMinor > 0 ? 'redeemed on bookings' : 'not yet in use'}
                />
              </>
            )}
          </section>

          <h2 className={styles.sectionTitle}>Breakdown</h2>
          <div className={styles.grid}>
            <Card>
              <CardHeader title="Booking outcomes" description="Every request, by what became of it" />
              <CardBody>
                {loading ? (
                  <ChartFallback />
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <OutcomeDonut report={report} />
                  </Suspense>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="By service type" description="City against Travel, split by outcome" />
              <CardBody>
                {loading ? (
                  <ChartFallback />
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <ServiceTypeChart data={report.serviceTypes} />
                  </Suspense>
                )}
              </CardBody>
            </Card>

            <Card className={styles.wide}>
              <CardHeader
                title="Bookings over time"
                description={
                  report?.trendGranularity === 'MONTH'
                    ? 'Monthly volume, stacked by outcome'
                    : 'Daily volume, stacked by outcome'
                }
              />
              <CardBody>
                {loading ? (
                  <ChartFallback height={280} />
                ) : (
                  <Suspense fallback={<ChartFallback height={280} />}>
                    <OutcomeTrendChart data={report.trend} granularity={report.trendGranularity} />
                  </Suspense>
                )}
              </CardBody>
            </Card>
          </div>

          <h2 className={styles.sectionTitle}>Response time</h2>
          <section className={styles.kpiGrid} aria-label="Response time figures">
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} height={132} />)
            ) : (
              <>
                <ReportStat
                  label="Median response"
                  value={formatDuration(report.responseTime.medianSeconds)}
                  icon={Timer}
                  tone="brand"
                  hint="half were answered faster"
                />
                <ReportStat
                  label="Average response"
                  value={formatDuration(report.responseTime.averageSeconds)}
                  icon={Clock}
                  tone="info"
                  hint="mean across every answer"
                />
                <ReportStat
                  label="90th percentile"
                  value={formatDuration(report.responseTime.p90Seconds)}
                  icon={Gauge}
                  tone="warning"
                  hint="slowest 1 in 10 sat beyond"
                />
                <ReportStat
                  label="Answered"
                  value={formatNumber(report.responseTime.respondedCount)}
                  icon={ClipboardCheck}
                  tone="neutral"
                  breakdown={[
                    { label: 'assigned', value: formatNumber(report.responseTime.assignedCount), tone: 'success' },
                    { label: 'rejected', value: formatNumber(report.responseTime.rejectedCount), tone: 'danger' },
                  ]}
                />
              </>
            )}
          </section>

          <div className={styles.grid}>
            <Card>
              <CardHeader
                title="Response time distribution"
                description="Answered requests, by how long they waited"
              />
              <CardBody>
                {loading ? (
                  <ChartFallback />
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <ResponseTimeChart data={report.responseTime.distribution} />
                  </Suspense>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="By employee" description="Who answered requests, and how quickly" />
              <CardBody>
                {loading ? (
                  <ChartFallback />
                ) : (
                  <EmployeeResponseTable data={report.responseTime.perEmployee} />
                )}
              </CardBody>
            </Card>
          </div>

          <p className={styles.footnote}>
            <strong>Response time</strong> is how long a request waited before the office answered it — a driver{' '}
            <strong>assigned</strong>, or a still-pending request <strong>rejected</strong> by a supervisor. Unlike the
            figures above, it is counted by the period the answer was <em>given</em> in, and grouped by whoever gave it.
            Rejections reach back across all history; per-employee assignment figures build up from when assignment
            attribution was added, so an early assignment may be absent rather than unattributed.
          </p>

          <p className={styles.footnote}>
            <strong>Fulfilled</strong> counts bookings whose trip completed; <strong>received</strong>,{' '}
            <strong>fulfilled</strong> and <strong>cancelled</strong> reconcile against the period a booking was
            received in. Cancellations are attributed from the record: <strong>staff</strong> when an operator
            cancelled from the dashboard, <strong>system</strong> for abandoned card checkouts the sweeper reclaimed,
            and <strong>customer</strong> for the rest. Revenue and refunds are in EGP; outstanding penalties are a
            live balance across all time rather than a figure for this period.
          </p>
        </>
      )}
    </>
  )
}
